"use client";

import "dayjs/locale/uk";

import { ConfigProvider, DatePicker, Flex } from "antd";
import ukUA from "antd/locale/uk_UA";
import dayjs, { Dayjs } from "dayjs";
import { FC, memo, useEffect, useMemo, useState } from "react";
import {
    getInvoiceGetSalesBySKUByPointIdsThunk,
    getInvoiceSalesByPointsThunk,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { BlockLoader } from "@/shared/ui/BlockLoader";
import { UniversalTabs } from "@/shared/ui/UniversalTabs";

import { ByPoint } from "./ByPoint";
import { BySKU } from "./BySKU";
import { StyledEmptySalesResultContainer } from "./EmptySalesResult/Styled";
import { TotalSumByAny } from "./TotalSumByAny";

type TabValue = "sku" | "market";

const salesTabsConfig: { value: TabValue; label: string }[] = [
    { value: "market", label: "По ТТ" },
    { value: "sku", label: "По СКЮ" },
];

dayjs.locale("uk");

interface ISalesProps {
    routeId: string;
}

const Sales: FC<ISalesProps> = ({ routeId }) => {
    const pointsSales = useAppSelector((state) => state.work.pointsSales);
    const [date, setDate] = useState<Dayjs>(dayjs());
    const dispatch = useAppDispatch();
    const [currentSalesTab, setCurrentSalesTab] = useState<TabValue>("market");
    const [loading, setLoading] = useState(false);

    const renderSalesTabContent = (tab: TabValue) => {
        if (loading) {
            return (
                <StyledEmptySalesResultContainer>
                    <BlockLoader />
                </StyledEmptySalesResultContainer>
            );
        }
        switch (tab) {
            case "market":
                return <ByPoint targetDate={date.toISOString()} />;
            case "sku":
                return <BySKU />;
            default:
                return null;
        }
    };

    const totalData = useMemo(() => {
        return pointsSales.reduce(
            (acc, point) => {
                point.invoices.forEach(({ paymentForm, amount }) => {
                    if (paymentForm === "1") acc.form1 += amount;
                    else if (paymentForm === "2") acc.form2 += amount;
                    acc.total += amount;
                });
                return acc;
            },
            { form1: 0, form2: 0, total: 0 },
        );
    }, [pointsSales]);

    useEffect(() => {
        const fetchSales = async () => {
            if (!date) return;
            setLoading(true);
            await dispatch(
                getInvoiceSalesByPointsThunk({
                    routeId,
                    targetDate: date.format(backendDateFormat.date),
                }),
            );
            await dispatch(
                getInvoiceGetSalesBySKUByPointIdsThunk({
                    routeId,
                    targetDate: date.format(backendDateFormat.date),
                }),
            );
            setLoading(false);
        };

        fetchSales();
    }, [date, dispatch, routeId]);

    return (
        <ConfigProvider locale={ukUA}>
            <Flex vertical gap={16}>
                <Flex gap={16}>
                    <UniversalTabs
                        value={currentSalesTab}
                        onChange={(val) => setCurrentSalesTab(val as TabValue)}
                        tabs={salesTabsConfig}
                    />

                    <DatePicker
                        value={date}
                        onChange={(val: Dayjs | null) => val && setDate(val)}
                        style={{ flex: 1 }}
                        format={() =>
                            date ? `${date.format("D MMMM YYYY")} року` : ""
                        }
                        allowClear={false}
                        disabledDate={(current) =>
                            current && current > dayjs().endOf("day")
                        }
                    />
                </Flex>

                {!loading && (
                    <TotalSumByAny data={[{ key: "total", ...totalData }]} />
                )}

                {renderSalesTabContent(currentSalesTab)}
            </Flex>
        </ConfigProvider>
    );
};

export default memo(Sales);
